import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useMobileOptimization } from '../hooks/useMobileOptimization';
import { 
  FORM_FIELD_TYPES,
  createStandardFieldConfig 
} from '../utils/editingInterface';
import './PersonDetailsPage.css';

const PersonDetailsPage = () => {
  const { personId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile, isTablet, isTouchDevice } = useMobileOptimization();
  
  // Get person data and gedcom data from location state or load from storage
  const [person, setPerson] = useState(location.state?.person || null);
  const [gedcomData, setGedcomData] = useState(location.state?.gedcomData || null);
  const [isEditing, setIsEditing] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);

  // Person editing field configurations
  const personFieldConfigs = {
    name: createStandardFieldConfig(FORM_FIELD_TYPES.TEXT, {
      label: 'Full Name',
      required: true,
      placeholder: 'Enter full name'
    }),
    sex: createStandardFieldConfig(FORM_FIELD_TYPES.SELECT, {
      label: 'Gender',
      options: [
        { value: 'M', label: 'Male' },
        { value: 'F', label: 'Female' }
      ]
    }),
    birth: createStandardFieldConfig(FORM_FIELD_TYPES.DATE, {
      label: 'Birth Date',
      placeholder: 'e.g., 1866, 15 Mar 1866'
    }),
    birthPlace: createStandardFieldConfig(FORM_FIELD_TYPES.LOCATION, {
      label: 'Birth Place',
      placeholder: 'e.g., Italy, Rome, Italy'
    }),
    death: createStandardFieldConfig(FORM_FIELD_TYPES.DATE, {
      label: 'Death Date',
      placeholder: 'e.g., 1958, 20 Dec 1958 (leave empty if living)'
    }),
    deathPlace: createStandardFieldConfig(FORM_FIELD_TYPES.LOCATION, {
      label: 'Death Place',
      placeholder: 'e.g., Italy, Toronto, Canada'
    }),
    note: createStandardFieldConfig(FORM_FIELD_TYPES.TEXTAREA, {
      label: 'Notes',
      placeholder: 'Add biographical notes, stories, or additional information...',
      rows: 4
    })
  };

  const [editedPerson, setEditedPerson] = useState({
    name: person?.name || '',
    birth: person?.birth || '',
    death: person?.death || '',
    birthPlace: person?.birthPlace || '',
    deathPlace: person?.deathPlace || '',
    sex: person?.sex || '',
    note: person?.note || ''
  });

  // Load data from localStorage if not provided in state
  useEffect(() => {
    if (!person || !gedcomData) {
      try {
        const storedGedcomData = localStorage.getItem('gedcomData');
        if (storedGedcomData) {
          const parsedData = JSON.parse(storedGedcomData);
          setGedcomData(parsedData);
          
          if (personId && parsedData.individuals && parsedData.individuals[personId]) {
            const foundPerson = parsedData.individuals[personId];
            setPerson(foundPerson);
            setEditedPerson({
              name: foundPerson.name || '',
              birth: foundPerson.birth || '',
              death: foundPerson.death || '',
              birthPlace: foundPerson.birthPlace || '',
              deathPlace: foundPerson.deathPlace || '',
              sex: foundPerson.sex || '',
              note: foundPerson.note || ''
            });
          }
        }
      } catch (error) {
        console.error('Error loading person data:', error);
      }
    }
  }, [personId, person, gedcomData]);

  // Update editedPerson when person changes
  useEffect(() => {
    if (person) {
      setEditedPerson({
        name: person.name || '',
        birth: person.birth || '',
        death: person.death || '',
        birthPlace: person.birthPlace || '',
        deathPlace: person.deathPlace || '',
        sex: person.sex || '',
        note: person.note || ''
      });
      setIsEditing(false);
      setSaveStatus(null);
    }
  }, [person]);

  if (!person || !gedcomData) {
  return (
    <div className={`page-container-wrapper max-width-default padding-default ${isMobile() ? 'mobile-optimized' : ''} ${isTouchDevice() ? 'touch-optimized' : ''}`}>
      <div className="page-header">
        <h1>
          <i className="fas fa-exclamation-triangle"></i>
          Person Not Found
        </h1>
        <p>The requested person could not be found</p>
      </div>
      
      <div className="page-content">
        <div className="section">
          <div className="person-not-found">
            <p>Sorry, the person you're looking for doesn't exist or the family tree data is not available.</p>
            <button 
              className="cta-btn primary"
              onClick={() => navigate('/family-tree')}
            >
              <i className="fas fa-arrow-left"></i>
              Back to Family Tree
            </button>
          </div>
        </div>
      </div>
    </div>
  );
  }

  const calculateAge = (birth, death) => {
    if (!birth) return null;
    
    const birthYear = parseInt(birth.match(/\d{4}/)?.[0]);
    if (!birthYear) return null;
    
    const endYear = death ? parseInt(death.match(/\d{4}/)?.[0]) : new Date().getFullYear();
    return endYear - birthYear;
  };

  const handleEditToggle = () => {
    if (isEditing) {
      // Reset to original values when canceling
      setEditedPerson({
        name: person.name || '',
        birth: person.birth || '',
        death: person.death || '',
        birthPlace: person.birthPlace || '',
        deathPlace: person.deathPlace || '',
        sex: person.sex || '',
        note: person.note || ''
      });
    }
    setIsEditing(!isEditing);
    setSaveStatus(null);
  };

  const handleInputChange = (field, value) => {
    setEditedPerson(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      setSaveStatus('saving');
      
      // Create updated person object
      const updatedPerson = {
        ...person,
        ...editedPerson
      };

      // Update local state to reflect changes
      Object.assign(person, editedPerson);
      
      // Save to localStorage
      const updatedGedcomData = {
        ...gedcomData,
        individuals: {
          ...gedcomData.individuals,
          [person.id]: updatedPerson
        }
      };
      localStorage.setItem('gedcomData', JSON.stringify(updatedGedcomData));
      setGedcomData(updatedGedcomData);
      
      setSaveStatus('success');
      setIsEditing(false);
      
      // Clear success message after 2 seconds
      setTimeout(() => setSaveStatus(null), 2000);
    } catch (error) {
      console.error('Error saving person data:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };

  const validateDate = (dateStr) => {
    if (!dateStr) return true;
    // Allow various date formats: YYYY, DD MMM YYYY, etc.
    const dateRegex = /^\d{4}$|^\d{1,2}\s+\w{3}\s+\d{4}$|^\w{3}\s+\d{4}$/;
    return dateRegex.test(dateStr.trim());
  };

  const isFormValid = () => {
    return editedPerson.name.trim() && 
           validateDate(editedPerson.birth) && 
           validateDate(editedPerson.death);
  };

  const getRelatives = () => {
    const relatives = {
      parents: [],
      spouses: [],
      children: [],
      siblings: []
    };

    // Find parents
    if (person.parents && gedcomData.families[person.parents]) {
      const parentFamily = gedcomData.families[person.parents];
      if (parentFamily.husband && gedcomData.individuals[parentFamily.husband]) {
        relatives.parents.push(gedcomData.individuals[parentFamily.husband]);
      }
      if (parentFamily.wife && gedcomData.individuals[parentFamily.wife]) {
        relatives.parents.push(gedcomData.individuals[parentFamily.wife]);
      }

      // Find siblings
      if (parentFamily.children) {
        relatives.siblings = parentFamily.children
          .filter(childId => childId !== person.id)
          .map(childId => gedcomData.individuals[childId])
          .filter(Boolean);
      }
    }

    // Find spouses and children
    person.families.forEach(familyId => {
      const family = gedcomData.families[familyId];
      if (family) {
        // Find spouse
        const spouseId = family.husband === person.id ? family.wife : family.husband;
        if (spouseId && gedcomData.individuals[spouseId]) {
          relatives.spouses.push({
            person: gedcomData.individuals[spouseId],
            marriageDate: family.marriage
          });
        }

        // Find children
        if (family.children) {
          const children = family.children
            .map(childId => gedcomData.individuals[childId])
            .filter(Boolean);
          relatives.children.push(...children);
        }
      }
    });

    return relatives;
  };

  const handlePersonSelect = (selectedPerson) => {
    navigate(`/person/${selectedPerson.id}`, {
      state: { person: selectedPerson, gedcomData }
    });
  };

  const handleFindRelationship = () => {
    navigate('/family-tree', {
      state: { 
        selectedPerson: person,
        showRelationshipFinder: true,
        gedcomData 
      }
    });
  };

  const relatives = getRelatives();
  const age = calculateAge(person.birth, person.death);

  return (
    <div className={`page-container-wrapper max-width-default padding-default ${isMobile() ? 'mobile-optimized' : ''} ${isTouchDevice() ? 'touch-optimized' : ''}`}>
      {/* Page Header */}
      <div className="page-header">
        <h1>
          <i className="fas fa-user"></i>
          Person Details
        </h1>
        <p>{person.name || 'Unknown Person'}</p>
        
        {/* Action Buttons */}
        <div className="page-header-actions">
          <button 
            className="cta-btn secondary"
            onClick={() => navigate('/family-tree', { state: { gedcomData } })}
          >
            <i className="fas fa-arrow-left"></i>
            Back to Family Tree
          </button>
          
          <button 
            className="cta-btn secondary"
            onClick={handleFindRelationship}
          >
            <i className="fas fa-route"></i>
            Find Relationship Path
          </button>
          
          <button 
            className={`cta-btn ${isEditing ? 'danger' : 'primary'}`}
            onClick={handleEditToggle}
          >
            <i className={`fas ${isEditing ? 'fa-times' : 'fa-edit'}`}></i>
            {isEditing ? 'Cancel Edit' : 'Edit Person'}
          </button>
        </div>
      </div>

      {/* Page Content */}
      <div className="page-content">
        <div className="section">
          <div className="person-details-container">
        {/* Person Header */}
        <div className="person-header">
          <div className={`person-avatar ${(isEditing ? editedPerson.sex : person.sex) === 'M' ? 'male' : 'female'}`}>
            <i className={`fas ${(isEditing ? editedPerson.sex : person.sex) === 'M' ? 'fa-mars' : 'fa-venus'}`}></i>
          </div>
          <div className="person-info">
            <h2>{isEditing ? editedPerson.name || 'Unknown Name' : person.name || 'Unknown Name'}</h2>
            <div className="person-dates">
              {(isEditing ? editedPerson.birth : person.birth) && (
                <span><i className="fas fa-birthday-cake"></i> Born: {isEditing ? editedPerson.birth : person.birth}</span>
              )}
              {(isEditing ? editedPerson.death : person.death) && (
                <span><i className="fas fa-cross"></i> Died: {isEditing ? editedPerson.death : person.death}</span>
              )}
              {age && <span><i className="fas fa-hourglass-half"></i> Age: {age} years</span>}
            </div>
          </div>
        </div>

        {/* Person Details */}
        <div className="person-content">
          <div className="details-grid">
            {/* Basic Information */}
            <div className="detail-section">
              <h3>
                <i className="fas fa-info-circle"></i> 
                Basic Information
                {isEditing && <span className="edit-indicator">✏️ Editing</span>}
              </h3>
              <div className="detail-items">
                <div className="detail-item">
                  <strong>Full Name:</strong>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedPerson.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="edit-input"
                      placeholder="Enter full name"
                      required
                    />
                  ) : (
                    <span>{person.name || 'Unknown'}</span>
                  )}
                </div>
                
                <div className="detail-item">
                  <strong>Gender:</strong>
                  {isEditing ? (
                    <select
                      value={editedPerson.sex}
                      onChange={(e) => handleInputChange('sex', e.target.value)}
                      className="edit-select"
                    >
                      <option value="">Unknown</option>
                      <option value="M">Male</option>
                      <option value="F">Female</option>
                    </select>
                  ) : (
                    <span>{person.sex === 'M' ? 'Male' : person.sex === 'F' ? 'Female' : 'Unknown'}</span>
                  )}
                </div>

                <div className="detail-item">
                  <strong>Birth Date:</strong>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedPerson.birth}
                      onChange={(e) => handleInputChange('birth', e.target.value)}
                      className="edit-input"
                      placeholder="e.g., 1866, 15 Mar 1866"
                    />
                  ) : (
                    <span>{person.birth || 'Unknown'}</span>
                  )}
                </div>

                <div className="detail-item">
                  <strong>Birth Place:</strong>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedPerson.birthPlace}
                      onChange={(e) => handleInputChange('birthPlace', e.target.value)}
                      className="edit-input"
                      placeholder="e.g., Italy, Rome, Italy"
                    />
                  ) : (
                    <span>{person.birthPlace || 'Unknown'}</span>
                  )}
                </div>

                <div className="detail-item">
                  <strong>Death Date:</strong>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedPerson.death}
                      onChange={(e) => handleInputChange('death', e.target.value)}
                      className="edit-input"
                      placeholder="e.g., 1958, 20 Dec 1958 (leave empty if living)"
                    />
                  ) : (
                    <span>{person.death || 'Living'}</span>
                  )}
                </div>

                <div className="detail-item">
                  <strong>Death Place:</strong>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedPerson.deathPlace}
                      onChange={(e) => handleInputChange('deathPlace', e.target.value)}
                      className="edit-input"
                      placeholder="e.g., Italy, Toronto, Canada"
                    />
                  ) : (
                    <span>{person.deathPlace || (person.death ? 'Unknown' : 'N/A')}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Family Information */}
            <div className="detail-section">
              <h3><i className="fas fa-users"></i> Family</h3>
              <div className="detail-items">
                {relatives.parents.length > 0 && (
                  <div className="detail-item">
                    <strong>Parents:</strong>
                    <ul className="relatives-list">
                      {relatives.parents.map(parent => (
                        <li key={parent.id}>
                          <button 
                            className="relative-link"
                            onClick={() => handlePersonSelect(parent)}
                          >
                            {parent.name}
                            <i className="fas fa-external-link-alt"></i>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {relatives.spouses.length > 0 && (
                  <div className="detail-item">
                    <strong>Spouse(s):</strong>
                    <ul className="relatives-list">
                      {relatives.spouses.map((spouse, index) => (
                        <li key={index}>
                          <button 
                            className="relative-link"
                            onClick={() => handlePersonSelect(spouse.person)}
                          >
                            {spouse.person.name}
                            <i className="fas fa-external-link-alt"></i>
                          </button>
                          {spouse.marriageDate && <span className="marriage-date"> (m. {spouse.marriageDate})</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {relatives.children.length > 0 && (
                  <div className="detail-item">
                    <strong>Children ({relatives.children.length}):</strong>
                    <ul className="relatives-list">
                      {relatives.children.map(child => (
                        <li key={child.id}>
                          <button 
                            className="relative-link"
                            onClick={() => handlePersonSelect(child)}
                          >
                            {child.name}
                            <i className="fas fa-external-link-alt"></i>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {relatives.siblings.length > 0 && (
                  <div className="detail-item">
                    <strong>Siblings ({relatives.siblings.length}):</strong>
                    <ul className="relatives-list">
                      {relatives.siblings.map(sibling => (
                        <li key={sibling.id}>
                          <button 
                            className="relative-link"
                            onClick={() => handlePersonSelect(sibling)}
                          >
                            {sibling.name}
                            <i className="fas fa-external-link-alt"></i>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Notes section */}
          {(person.note || isEditing) && (
            <div className="detail-section full-width">
              <h3><i className="fas fa-sticky-note"></i> Notes</h3>
              {isEditing ? (
                <textarea
                  value={editedPerson.note}
                  onChange={(e) => handleInputChange('note', e.target.value)}
                  className="edit-textarea"
                  placeholder="Add biographical notes, stories, or additional information..."
                  rows="4"
                />
              ) : (
                <div className="person-notes">
                  {person.note}
                </div>
              )}
            </div>
          )}

          {/* Save/Cancel buttons for editing mode */}
          {isEditing && (
            <div className="edit-actions">
              <button 
                className="cta-btn primary" 
                onClick={handleSave}
                disabled={!isFormValid() || saveStatus === 'saving'}
              >
                <i className={`fas ${saveStatus === 'saving' ? 'fa-spinner fa-spin' : 'fa-save'}`}></i>
                {saveStatus === 'saving' ? 'Saving...' : 'Save Changes'}
              </button>
              <button className="cta-btn secondary" onClick={handleEditToggle}>
                <i className="fas fa-times"></i>
                Cancel
              </button>
            </div>
          )}
          
          {/* Save Status Messages */}
          {saveStatus === 'success' && (
            <div className="save-status success">
              <i className="fas fa-check-circle"></i>
              Changes saved successfully!
            </div>
          )}
          {saveStatus === 'error' && (
            <div className="save-status error">
              <i className="fas fa-exclamation-circle"></i>
              Error saving changes. Please try again.
            </div>
          )}
        </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default PersonDetailsPage;